import React from 'react'
import PropTypes from 'prop-types'

import { rgba } from 'polished'

import styled from '@emotion/styled'

import MediaImage from '../../Generics/Media/MediaImage'
import VideoThumb from '../../Generics/VideoThumb'

import { PRODUCT_AVAILABILITY } from '../../../../helpers/const'
import Availability from '../ProductAvailability'

const StyledRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const BaseTypography = styled.p(({ theme: { spaces, fontSize } }) => ({
  margin: 0,
  paddingRight: spaces.default / 2,
  fontWeight: '800',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: `${fontSize * 0.9375}px`,
  lineHeight: `${fontSize * 1.375}px`,
}))

const StyledAvailability = styled.span(({ theme: { colors } }) => ({
  backgroundColor: colors.white,
  borderTopRightRadius: '10px',
  borderBottomRightRadius: '10px',
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '1px 6px',
  position: 'absolute',
  top: '12px',
}))

const StyledThumbnail = styled.div(({ theme: { colors, spaces } }) => ({
  position: 'relative',
  boxShadow: `inset 0 1px 50px ${rgba(colors.mid_grey, 0.4)}`,
  paddingBottom: '100%',
  marginBottom: spaces.default / 8,
  '& img, & div': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: -1,
  },
}))

const StyledTitle = styled(BaseTypography)(({ theme: { colors } }) => ({
  color: colors.dark_grey,
}))

const StyledPrice = styled(BaseTypography)(({ theme: { colors } }) => ({
  color: colors.primary,
  whiteSpace: 'initial',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
}))

const StyledSale = styled(BaseTypography)(({ theme: { colors } }) => ({
  color: colors.primary,
}))

const StyledRealPrice = styled(BaseTypography)(({ theme: { colors } }) => ({
  fontWeight: '700',
  color: colors.mid_grey,
  textDecoration: 'line-through',
}))

const Card = ({
  containerRef,
  previewRef,
  hasVideo,
  mediaSrc,
  title,
  price,
  sale,
  realPrice,
  onImageLoaded,
  onClick,
  availability,
  isSimilarProductsCarousel,
}) => (
  <StyledRoot onClick={onClick} ref={previewRef} data-test="Card">
    <StyledThumbnail>
      {
        hasVideo
          ? <VideoThumb containerRef={containerRef} data-test="PreviewImage" src={mediaSrc} onLoad={onImageLoaded} size={isSimilarProductsCarousel ? 40 : 80} />
          : <MediaImage containerRef={containerRef} data-test="PreviewImage" src={mediaSrc} onLoad={onImageLoaded} />
      }
      { PRODUCT_AVAILABILITY[availability]?.visiblePreview ? (
        <StyledAvailability>
          <Availability availability={availability} />
        </StyledAvailability>
      ) : null}
    </StyledThumbnail>
    <StyledTitle data-test="PreviewTitle">{title}</StyledTitle>
    { price ? <StyledPrice data-test="PreviewPrice">{price}</StyledPrice> : null }
    { realPrice ? <StyledRealPrice data-test="PreviewDiscountPrice">{realPrice}</StyledRealPrice> : null }
    <StyledSale data-test="PreviewSale">{sale}</StyledSale>
  </StyledRoot>
)

Card.propTypes = {
  containerRef: PropTypes.shape({}),
  previewRef: PropTypes.shape({}),
  hasVideo: PropTypes.bool,
  mediaSrc: PropTypes.string,
  title: PropTypes.string,
  price: PropTypes.string,
  availability: PropTypes.string,
  sale: PropTypes.string,
  realPrice: PropTypes.string,
  onImageLoaded: PropTypes.func,
  onClick: PropTypes.func,
  isSimilarProductsCarousel: PropTypes.bool,
}

Card.defaultProps = {
  containerRef: null,
  previewRef: { current: null },
  hasVideo: false,
  mediaSrc: null,
  title: '',
  price: '',
  availability: '',
  sale: '',
  realPrice: '',
  isSimilarProductsCarousel: false,
  onImageLoaded: () => false,
  onClick: () => false,
}

Card.displayName = 'Card'
Card.whyDidYouRender = true

export default React.memo(Card)
